import React, { useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, TagIcon, PercentIcon, DollarSignIcon } from 'lucide-react';
import { CampaignList } from '../components/CampaignList';
import { Campaign } from '../../../shared/types/Campaign';
// Mock campaign data based on screenshots
const mockCampaigns: Campaign[] = [{
  id: 'BILASHOP2025',
  title: '[BILA Mart] 7-days Promotion for Shop',
  description: '[BILA Mart] 7-days Promotion for Shop',
  expiryDate: '07/03/2026',
  discountPercentage: 0,
  fixedDiscountAmount: 50,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 5000,
  budgetUsed: 0,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'BILASHOP2025',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 50,
  maxDiscount: 50,
  status: 'active',
  companies: ['Maybank Life'],
  transactionVolume: 47250,
  transactionCount: 63
}, {
  id: 'BILA10MYR',
  title: `BILA Mart Special Package (${getTenant().currency.code} 10 Discount)`,
  description: 'DISCOUNT_COUPON',
  expiryDate: '02/01/2026',
  discountPercentage: 0,
  fixedDiscountAmount: 10,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 11000,
  budgetUsed: 10960,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'BILA10MYR',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 10,
  maxDiscount: 10,
  status: 'active',
  transactionVolume: 1284500,
  transactionCount: 1096
}, {
  id: 'BULK2025',
  title: 'Summer Festival Bulk Codes',
  description: 'Unique codes for summer festival attendees',
  startDate: '01/11/2025',
  expiryDate: '31/01/2026',
  discountPercentage: 15,
  fixedDiscountAmount: 0,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budgetType: 'fixed',
  budget: 50000,
  budgetUsed: 12500,
  strictlyEnforceBudget: true,
  paymentMethods: ['Card', 'PromptPay'],
  cardTargetingMode: 'all',
  cardBrands: [],
  cardBin: '',
  cardIssuingCountry: '',
  cardType: 'All Types',
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: '',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Bulk',
  minTransactionValue: 500,
  maxTransactionValue: 999999,
  minDiscount: 0,
  maxDiscount: 1000,
  status: 'active',
  transactionVolume: 312750,
  transactionCount: 245
}];
export function Campaigns() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const handleCreateCampaign = () => {
    navigate('/campaign-form');
  };
  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaign-details?id=${campaignId}`);
  };
  const filteredCampaigns = mockCampaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.id.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Calculate summary metrics
  const activeCampaigns = mockCampaigns.filter(c => c.status === 'active');
  const totalBudget = activeCampaigns.reduce((sum, c) => sum + c.budget, 0);
  const totalUsed = activeCampaigns.reduce((sum, c) => sum + c.budgetUsed, 0);
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-10996">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-10997">
        <div data-id="element-10998">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-10999">
            {t('pages.Campaigns.campaigns')}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-11000">
            {t('pages.Campaigns.manage_your_discount_codes_and_promotional')}
          </p>
        </div>
        <button onClick={handleCreateCampaign} className="px-4 py-2 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11001">
          {t('pages.Campaigns.create_campaign')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-11002">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11003">
          <div className="flex items-center gap-3 mb-2" data-id="element-11004">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg" data-id="element-11005">
              <TagIcon size={20} data-id="element-11006" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-11007">
              {t('pages.Campaigns.active_campaigns')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-11008">
            {activeCampaigns.length}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11009">
          <div className="flex items-center gap-3 mb-2" data-id="element-11010">
            <div className="p-2 bg-green-50 text-green-600 rounded-lg" data-id="element-11011">
              <PercentIcon size={20} data-id="element-11012" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-11013">
              {t('pages.Campaigns.total_redemptions')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-11014">1,248</div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11015">
          <div className="flex items-center gap-3 mb-2" data-id="element-11016">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg" data-id="element-11017">
              <DollarSignIcon size={20} data-id="element-11018" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-11019">
              {t('pages.Campaigns.budget_allocated')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-11020">
            {t('components.ShopDashboard.usd_2')} {totalBudget.toLocaleString()}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11021">
          <div className="flex items-center gap-3 mb-2" data-id="element-11022">
            <div className="p-2 bg-[#FFFBE6] text-[#B38A00] rounded-lg" data-id="element-11023">
              <DollarSignIcon size={20} data-id="element-11024" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-11025">
              {t('pages.Campaigns.budget_used')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-11026">
            {t('components.ShopDashboard.usd_2')} {totalUsed.toLocaleString()}
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3 overflow-hidden" data-id="element-11027">
            <div className="bg-[#FFC70A] h-1.5 rounded-full" style={{
            width: `${totalUsed / totalBudget * 100}%`
          }} data-id="element-11028"></div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-4 justify-between items-center" data-id="element-11029">
        <div className="relative w-full sm:w-96" data-id="element-11030">
          <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-11031" />

          <input type="text" placeholder={t('pages.Campaigns.attrs.search_campaigns_by_name_id_or')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] focus:outline-none transition-colors" data-id="element-11032" />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-11033">
          <span className="text-sm text-gray-500 font-medium" data-id="element-11034">
            {t('pages.Campaigns.status')}
          </span>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] focus:outline-none transition-colors" data-id="element-11035">
            <option value="all" data-id="element-11036">{t('pages.Campaigns.all_statuses')}</option>
            <option value="active" data-id="element-11037">{t('pages.Campaigns.active')}</option>
            <option value="inactive" data-id="element-11038">{t('pages.Campaigns.inactive')}</option>
            <option value="expired" data-id="element-11039">{t('pages.Campaigns.expired')}</option>
          </select>
        </div>
      </div>

      {/* Campaign List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11040">
        <CampaignList campaigns={filteredCampaigns} onCampaignClick={handleCampaignClick} data-id="element-11041" />
      </div>
    </div>;
}